import type { FederatedPointerEvent } from 'pixi.js';
import { Container, Ticker } from 'pixi.js';
import { Signal } from 'typed-signals';
import { DioramaHeaderView } from 'game/views/dioramas/DioramaHeaderView';
import { DioramaSwitchLayout } from '../../../layouts/mainMenu/home/DioramaSwitchLayout';
import type { AppStateController } from '../../app/state/AppStateController';
import type { DioramaConfig } from '../../app/state/diorama/dioramaState';
import { registerResizableObject } from '../../utils/pixi/registerResizableObject';
import { dioramaConfigs } from '../../app/state/diorama/DioramaConfig';
import { DioramaDisplayOptionView } from './DioramaDisplayOptionView';
import type { Preload } from '../../app/loader/Preload';
import { isHorizontal } from '../../app/RendererPlugin';

interface DioramaOptionView extends Container {
  isCompleted(): boolean;
  activate?: () => void;
}

interface DioramaOption {
  dioramaConfig: DioramaConfig;
  view: DioramaOptionView;
}

export interface DioramaDisplayViewConfig {
  state: AppStateController;
  preloader: Preload;
  canShare: boolean;
}

export class DioramaDisplayView extends Container {
  private readonly positionDistance = 700;

  private readonly draggableDistance = 200;

  public readonly onCloseButtonClicked = new Signal();

  public readonly onDioramaShare = new Signal<(config: DioramaConfig) => void>();

  private readonly layout = new DioramaSwitchLayout();

  private readonly dioramaHeaderView: DioramaHeaderView;

  private readonly options: DioramaOption[];

  private readonly totalDioramas: number;

  private currentOptionIndex = 0;

  private swipeStartX = 0;

  private isSwiping = false;

  private animating = false;

  constructor(private readonly config: DioramaDisplayViewConfig) {
    super();

    this.options = this.getAvailableOptions();
    this.totalDioramas = this.options.length;

    this.dioramaHeaderView = new DioramaHeaderView(this.layout.ribbon, this.config.state.getCurrentDioramaState());

    void this.init();

    registerResizableObject(this);
  }

  private getAvailableOptions(): DioramaOption[] {
    const {
      config: { state, preloader },
      layout: { dioramaSlot }
    } = this;

    const { dioramaIndex, tilesAmount } = state.getCurrentDioramaState();
    //return dioramaConfigs.slice(0, dioramaIndex + 1).map((dioramaConfig, index) => {
    return dioramaConfigs.map((dioramaConfig, index) => {
      const isLocked = index > dioramaIndex;
      let progress = 0;
      if (!isLocked) {
        progress = dioramaIndex === index ? tilesAmount : Infinity;
      }

      const view = 
        new DioramaDisplayOptionView({
          dioramaConfig,
          progress,
          isLocked,
          preloader,
        }).props({
          parent: dioramaSlot,
          visible: false,
        });
      
      return {
        dioramaConfig,
        view,
      };
    });
  }

  private async init(): Promise<void> {
    this.interactiveChildren = false;

    const {
      layout: {
        dioramaModeCloseButton,
        root,
        dioramaSlot,
        dioramaShareButton: { root: dioramaShareButton },
        nextButton: { root: nextButton },
        prevButton: { root: prevButton }
      },
      config: { canShare, state },
    } = this;

    root.props({ parent: this });

    dioramaShareButton.visible = canShare;

    dioramaModeCloseButton.on('pointertap', () => {
      this.onCloseButtonClicked.emit();
    });

    dioramaShareButton.on('pointertap', () => {
      if (!this.options[this.currentOptionIndex].view.isCompleted()) return;
      this.onDioramaShare.emit(this.options[this.currentOptionIndex].dioramaConfig);
    });

    nextButton.on('pointertap', () => {
      const nextConfig = this.options[this.currentOptionIndex + 1];
      if (!nextConfig) return;
      void this.setCurrentOptionAnimated(this.currentOptionIndex + 1);
    });

    prevButton.on('pointertap', () => {
      if (this.currentOptionIndex === 0) return;
      void this.setCurrentOptionAnimated(this.currentOptionIndex - 1);
    });
    
    this.setOptionIndex(state.getCurrentDioramaState().dioramaIndex);

    this.visible = false;

    dioramaSlot.on('pointerdown', this.onSwipeStart.bind(this));
    dioramaSlot.on('pointermove', this.onSwipeMove.bind(this));
    dioramaSlot.on('pointerup', this.onSwipeEnd.bind(this));
    dioramaSlot.on('pointerupoutside', this.onSwipeEnd.bind(this));
  }

  public async show(): Promise<void> {
    this.visible = true;
    await this.tweenFrom({ alpha: 0 }, 200, 'sinOut', true).asyncOnComplete();
    this.interactiveChildren = true;

    this.initTicker();
  }

  private initTicker(): void {
    const ticker = new Ticker();
    const callback = (): void => {
      this.update(ticker.deltaTime);
    };
    ticker.add(callback);
    ticker.start();

    this.on('destroyed', () => {
      ticker.destroy();
    });
  }

  private setOptionIndex(optionIndex: number): void {
    const option = this.options[optionIndex];
    if (!option) return;

    const { view } = option;
    const {
      nextButton: { root: nextButton },
      prevButton: { root: prevButton },
      dioramaShareButton: { root: dioramaShareButton },
    } = this.layout;

    this.currentOptionIndex = optionIndex;
    view.activate?.();
    nextButton.visible = !!this.options[optionIndex + 1];
    prevButton.visible = optionIndex !== 0;

    this.dioramaHeaderView.updateView(optionIndex, view.isCompleted());

    dioramaShareButton.alpha = view.isCompleted() ? 1 : 0;
    dioramaShareButton.eventMode = view.isCompleted() ? 'static' : 'none';
  }

  private async setCurrentOptionAnimated(optionIndex: number): Promise<void> {
    if (this.currentOptionIndex === optionIndex) return;
    this.animating = true;

    const prev = this.options[this.currentOptionIndex];
    const next = this.options[optionIndex];
    if (!next) return;

    const {
      ribbon: { root: ribbonContainer },
      dioramaShareButton: { root: dioramaShareButton },
    } = this.layout;

    const animateToLeft = optionIndex > this.currentOptionIndex;

    next.view.activate?.();

    prev.view.tweenTo({ x: this.positionDistance * (animateToLeft ? -1 : 1), alpha: 0 }, 400, 'sinOut', true);
    next.view.tweenTo({ x: 0, alpha: 1 }, 400, 'sinOut', true);

    dioramaShareButton.tweenTo({ alpha: next.view.isCompleted() ? 1 : 0 }, 150, 'sinIn', true);

    await ribbonContainer.tweenTo({ alpha: 0 }, 150, 'sinIn', true).asyncOnComplete();

    this.setOptionIndex(optionIndex);

    await ribbonContainer.tweenTo({ alpha: 1 }, 150, 'sinOut', true).asyncOnComplete();

    this.animating = false;
  }

  public async closeAndDestroy(): Promise<void> {
    this.interactiveChildren = false;
    this.destroy({ children: true });
    this.options.forEach(({ view }) => {
      view.destroy({ children: true });
    });
  }

  public resize(width: number, height: number): void {
    const { 
      buttonsContainer, 
      dioramaSlot, 
      nextButton: { root: nextButton },
      prevButton: { root: prevButton },
      ribbon: { root: ribbon }
     } = this.layout;

    const isHorizontalLayout = isHorizontal(width);

    dioramaSlot.props({
      x: Math.round(width * 0.5),
      y: isHorizontalLayout ? Math.round(height * 0.7) : Math.round(height * 0.6),
      scale: isHorizontalLayout ? 1.1 : 0.87,
    });

    buttonsContainer.props({
      x: dioramaSlot.x,
      y: dioramaSlot.y + 40,
    });

    const swithButtonOffsetX = isHorizontalLayout ? 500 : 400;
    prevButton.props({
      x: Math.max(width * 0.1, dioramaSlot.x - swithButtonOffsetX),
      y: dioramaSlot.y - 200,
    });
    nextButton.props({
      x: Math.min(width * 0.9, dioramaSlot.x + swithButtonOffsetX),
      y: dioramaSlot.y - 200,
    });

    const ribbonOffsetY = isHorizontalLayout ? height * 0.58 : 650;
    ribbon.position.set(dioramaSlot.x, dioramaSlot.y - ribbonOffsetY);
  }

  private onSwipeStart(event: FederatedPointerEvent): void {
    this.swipeStartX = event.global.x;
    this.isSwiping = true;
  }

  private onSwipeMove(event: FederatedPointerEvent): void {
    if (this.destroyed) return;
    if (!this.isSwiping) return;

    const prev = this.options[this.currentOptionIndex - 1] || null;
    const current = this.options[this.currentOptionIndex];
    const next = this.options[this.currentOptionIndex + 1] || null;

    const deltaX = event.global.x - this.swipeStartX;

    if (current.view?.destroyed || next?.view?.destroyed) return;

    if (prev) prev.view.alpha = 0;
    if (next) next.view.alpha = 0;

    if (deltaX < 0) {
      current.view.x = deltaX;

      if (deltaX < -this.draggableDistance / 2 && next?.view) {
        next.view.activate?.();
        next.view.x = deltaX + this.positionDistance;
        next.view.alpha = 1 - (this.draggableDistance - Math.abs(deltaX)) / (this.draggableDistance / 2);
      }
    } else if (deltaX > 0) {
      current.view.x = deltaX;

      if (deltaX > this.draggableDistance / 2 && prev?.view) {
        prev.view.activate?.();
        prev.view.x = deltaX - this.positionDistance;
        prev.view.alpha = 1 - (this.draggableDistance - deltaX) / (this.draggableDistance / 2);
      }
    }
  }

  private async onSwipeEnd(event: FederatedPointerEvent): Promise<void> {
    if (!this.isSwiping) return;
    this.isSwiping = false;

    const deltaX = event.global.x - this.swipeStartX;

    if (deltaX < -this.draggableDistance && this.currentOptionIndex < this.totalDioramas - 1) {
      void this.setCurrentOptionAnimated(this.currentOptionIndex + 1);
    } else if (deltaX > this.draggableDistance && this.currentOptionIndex > 0) {
      void this.setCurrentOptionAnimated(this.currentOptionIndex - 1);
    }
  }

  private update(deltaTime: number): void {
    if (this.isSwiping || this.animating) return;

    const prev = this.options[this.currentOptionIndex - 1] || null;
    const current = this.options[this.currentOptionIndex];
    const next = this.options[this.currentOptionIndex + 1] || null;

    if (current) {
      if (Math.abs(current.view.position.x) - 1 < 0) {
        current.view.position.x = 0;
      }

      if (Math.abs(current.view.position.x) > 0) {
        current.view.position.x += (0 - current.view.position.x) * 0.1 * deltaTime;
      }
    }

    if (prev) {
      if (Math.abs(prev.view.position.x + this.positionDistance) - 1 < 0) {
        prev.view.position.x = -this.positionDistance;
      }

      if (Math.abs(prev.view.position.x + this.positionDistance) > 0) {
        prev.view.position.x += (-this.positionDistance - prev.view.position.x) * 0.1 * deltaTime;
      }

      prev.view.alpha =
        1 -
        (this.draggableDistance - Math.abs(prev.view.position.x + this.positionDistance)) /
          (this.draggableDistance / 2);
    }

    if (next) {
      if (Math.abs(next.view.position.x - this.positionDistance) - 1 < 0) {
        next.view.position.x = this.positionDistance;
      }

      if (Math.abs(next.view.position.x - this.positionDistance) > 0) {
        next.view.position.x += (this.positionDistance - next.view.position.x) * 0.1 * deltaTime;
      }

      next.view.alpha =
        1 -
        (this.draggableDistance - Math.abs(next.view.position.x - this.positionDistance)) /
          (this.draggableDistance / 2);
    }
  }
}
